import { useGetUsers } from "@/api/queries/users";
import type { UserItem } from "@/api/queries/users/types";
import { TablePagination } from "@/components/table/table-pagination";
import { type SortDirection, TableSort } from "@/components/table/table-sort";
import { Badge } from "@/components/ui/badge";
import { DatePickerWithRange } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  type TableColumn,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ROLES } from "@/constants/users";
import { formatDate } from "@/lib/date";
import { Fragment, useMemo } from "react";

import { useUsersStore } from "../hooks/use-users-state";
import { UserStatus } from "./cells/user-status";
import { ResetPasswordDialog } from "./reset-password-dialog";

const rolesLabels = {
  [ROLES.ADMIN]: "Admin",
  [ROLES.AGENT]: "Agent",
};

export const UsersTable = () => {
  const { state, setState } = useUsersStore();
  const { data, isLoading } = useGetUsers(state);

  const columns = useMemo<TableColumn<UserItem>[]>(() => {
    return [
      {
        key: "username",
        className: "min-w-[180px] md:min-w-unset",
        label: (
          <Input
            placeholder="Username"
            value={state.username}
            onChange={(e) => setState({ username: e.target.value })}
          />
        ),
        sortable: true,
        render: (user) => <TableCell className="font-medium">{user.username}</TableCell>,
      },
      {
        key: "role",
        label: (
          <Select
            value={state.role ?? null}
            onValueChange={(role) => setState({ role: role ?? undefined })}
          >
            <SelectTrigger>{state.role ? rolesLabels[state.role] : "Role"}</SelectTrigger>
            <SelectContent>
              <SelectItem value={null}>Select Status</SelectItem>
              <SelectItem value={ROLES.ADMIN}>Admin</SelectItem>
              <SelectItem value={ROLES.AGENT}>Agent</SelectItem>
            </SelectContent>
          </Select>
        ),
        sortable: true,
        render: (user) => (
          <TableCell>
            <Badge variant={user.role === "ADMIN" ? "default" : "secondary"}>{user.role}</Badge>
          </TableCell>
        ),
      },
      {
        key: "status",
        label: (
          <Select
            value={state.active ? "active" : state.active === undefined ? null : "inactive"}
            onValueChange={(status) =>
              setState({
                active: status ? status === "active" : undefined,
              })
            }
          >
            <SelectGroup>
              <SelectTrigger>
                {state.active ? "Active" : state.active === undefined ? "Status" : "Inactive"}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>Select Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </SelectGroup>
          </Select>
        ),
        sortable: true,
        render: (user) => <UserStatus user={user} />,
      },
      {
        key: "lastLoginAt",
        label: "Last Login",
        sortable: true,
        render: (user) => (
          <TableCell>
            {user.lastLoginAt ? formatDate(new Date(user.lastLoginAt)) : "Never"}
          </TableCell>
        ),
      },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        render: (user) => <TableCell>{formatDate(new Date(user.createdAt))}</TableCell>,
      },
      {
        key: "actions",
        label: "Actions",
        className: "text-right",
        render: (user) => (
          <TableCell className="text-right">
            <div className="flex justify-end gap-2">
              <ResetPasswordDialog userId={user.id} />
            </div>
          </TableCell>
        ),
      },
    ];
  }, [state]);

  const handleSortChange = (key: string) => {
    return (sortDirection: "asc" | "desc" | undefined) => {
      setState({ sortBy: key, orderBy: sortDirection });
    };
  };

  return (
    <>
      <div className="flex flex-wrap gap-4 mb-8 justify-between md:justify-start">
        <div className="min-w-[180px]">
          <DatePickerWithRange
            date={{
              from: state.minLastLoginAt ? new Date(state.minLastLoginAt) : undefined,
              to: state.maxLastLoginAt ? new Date(state.maxLastLoginAt) : undefined,
            }}
            onSelect={(range) =>
              setState({
                minLastLoginAt: range?.from?.toISOString(),
                maxLastLoginAt: range?.to?.toISOString(),
              })
            }
            label="Last Login"
          />
        </div>
        <div className="min-w-[180px]">
          <DatePickerWithRange
            date={{
              from: state.minCreatedAt ? new Date(state.minCreatedAt) : undefined,
              to: state.maxCreatedAt ? new Date(state.maxCreatedAt) : undefined,
            }}
            onSelect={(range) =>
              setState({
                minCreatedAt: range?.from?.toISOString(),
                maxCreatedAt: range?.to?.toISOString(),
              })
            }
            label="Created"
          />
        </div>
      </div>
      <div className="min-w-0 overflow-hidden flex">
        <div className="rounded-xl border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col.key} className={col.className}>
                    <div className="flex items-center gap-1">
                      {col.label}{" "}
                      {col.sortable && (
                        <TableSort
                          onChange={handleSortChange(col.key)}
                          direction={
                            col.key === state.sortBy ? (state.orderBy as SortDirection) : undefined
                          }
                        />
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <div className="flex items-center justify-center p-8">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (data?.data || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <div className="flex items-center justify-center p-8 text-muted-foreground">
                      No users found
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {(data?.data || []).map((user) => (
                <TableRow key={user.id}>
                  {columns.map((col) => (
                    <Fragment key={col.key}>{col.render(user)}</Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end">
            <TablePagination
              page={state.page ?? 1}
              pageSize={state.pageSize ?? 20}
              onPageSizeChange={(pageSize) => setState({ pageSize })}
              onPageChange={(page) => setState({ page })}
              total={data?.pagination.total ?? 1}
            />
          </div>
        </div>
      </div>
    </>
  );
};
